/**
 * card.ts - Tournament card rendering
 *
 * Creates complete tournament cards with all tabs and UI elements
 */

import type { Tournament, Category, TiebreakCriterion } from '../../types/api';
import { TIEBREAK_DEFINITIONS, ALL_TIEBREAK_KEYS, DEFAULT_TIEBREAKS_BY_FORMAT, MAX_TIEBREAK_CRITERIA } from '../../types/api';
import { TOURNAMENT_FORMATS, DEFAULTS } from '../utils/constants';
import { escapeHtml } from '../utils/security';
import { formatTournamentDate, formatRoundDateTime, simplifyVenueName } from '../utils/formatting';
import { t } from '../utils/i18n-helper';
import { renderCategoriesSection, loadCategories, getCachedCategories } from './categories';

/**
 * Get the saved player form mode from localStorage
 * @returns The saved mode ('simple' or 'advanced'), defaults to 'simple'
 */
function getSavedPlayerFormMode(): string {
  try {
    return localStorage.getItem('playerFormMode') || 'simple';
  } catch (error) {
    return 'simple';
  }
}

/**
 * Get the saved player sort order from localStorage
 * @returns The saved sort order ('initialRanking', 'firstName', or 'lastName'), defaults to 'initialRanking'
 */
function getSavedPlayerSortOrder(): string {
  try {
    return localStorage.getItem('playerSortOrder') || 'initialRanking';
  } catch (error) {
    return 'initialRanking';
  }
}

/**
 * Sort players by the current sort order
 * @param players - Array of players to sort
 * @returns Sorted copy of the players array
 */
function getSortedPlayers(players: import('../../types/api').Player[]): import('../../types/api').Player[] {
  const sortOrder = getSavedPlayerSortOrder();
  const sorted = [...players];

  switch (sortOrder) {
    case 'firstName':
      sorted.sort((a, b) => a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase()));
      break;
    case 'lastName':
      sorted.sort((a, b) => a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase()));
      break;
    case 'initialRanking':
    default:
      // Sort by initialRanking (ascending), with unranked players at the end
      sorted.sort((a, b) => {
        const rankA = a.initialRanking ?? 9999;
        const rankB = b.initialRanking ?? 9999;
        return rankA - rankB;
      });
      break;
  }

  return sorted;
}

/**
 * Format a currency amount for display
 * @param amount - Amount in cents
 * @param currency - Currency code (e.g., 'USD', 'CHF')
 * @returns Formatted currency string
 */
function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(amount / 100);
}

/**
 * Sort players in the player list (DOM operation)
 * @param tournamentId - The tournament ID
 * @param sortBy - The sort criteria ('initialRanking', 'firstName', or 'lastName')
 */
export function sortPlayers(tournamentId: string, sortBy: string): void {
  // Save preference
  try {
    localStorage.setItem('playerSortOrder', sortBy);
  } catch (error) {
    // Ignore storage errors
  }

  const list = document.getElementById(`player-list-ul-${tournamentId}`);
  if (!list) return;

  const rows = Array.from(list.querySelectorAll('.player-row')) as HTMLElement[];

  rows.sort((a, b) => {
    switch (sortBy) {
      case 'firstName':
        return (a.dataset.firstname || '').localeCompare(b.dataset.firstname || '');
      case 'lastName':
        return (a.dataset.lastname || '').localeCompare(b.dataset.lastname || '');
      case 'initialRanking':
      default:
        const rankA = parseInt(a.dataset.initialranking || '9999', 10);
        const rankB = parseInt(b.dataset.initialranking || '9999', 10);
        return rankA - rankB;
    }
  });

  // Re-append in sorted order
  rows.forEach(row => list.appendChild(row));
}

/**
 * Create a simplified tournament list card for the tournaments list view
 * Grid-based panel similar to directory view
 * Shows: logo, name, status, format, date, venue, players, rounds
 * @param tournament - Tournament object
 * @returns HTML string for simplified tournament list card
 */
export function createTournamentListCard(tournament: Tournament): string {
  const statusClass = `status-${tournament.status}`;
  const playersCount = tournament.players.length;
  const matchesCount = tournament.matches.length;

  // Calculate the highest round number from matches
  const highestRound = matchesCount > 0
    ? Math.max(...tournament.matches.map(m => m.round))
    : 0;

  // Show owner controls if: tournament has no userId (legacy/unauthenticated) OR user is the owner (has userId and not shared)
  const isOwner = !tournament.userId || (tournament.userId && !tournament.sharedBy);
  const isShared = !!tournament.sharedBy;
  const isPubliclyShared = !!tournament.shareToken || !!tournament.published;

  // Translate format labels
  const formatKey = tournament.format
    ? `tournament.format.${tournament.format.split('-').map((part, i) => i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)).join('')}`
    : 'tournament.format.label';
  const formatLabel = tournament.format
    ? t(formatKey, tournament.format.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' '))
    : t('tournament.format.label', 'Format');
  const formatDisplay = escapeHtml(formatLabel);

  // Translate status
  const statusKey = `status.${tournament.status}`;
  const statusText = t(statusKey, tournament.status);

  // Format dates - simplified
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  let dateDisplay = '';
  if (tournament.startDate) {
    dateDisplay = formatDate(tournament.startDate);
    if (tournament.endDate && tournament.endDate !== tournament.startDate) {
      dateDisplay += ` - ${formatDate(tournament.endDate)}`;
    }
  }

  return `
    <div class="tournament-card${isPubliclyShared ? ' tournament-card-public' : ''}" onclick="window.location.href='/tournament.html?id=${tournament.id}'" style="cursor: pointer; padding: 24px; background: white; border: 1px solid #dcdcdc; border-radius: 12px;">
      <div class="tournament-card-logo">
        <img src="${tournament.logo || DEFAULTS.TOURNAMENT_LOGO}" alt="${escapeHtml(tournament.name)}" onerror="this.src='${DEFAULTS.TOURNAMENT_LOGO}'">
      </div>

      <div class="tournament-card-header">
        <h3>${escapeHtml(tournament.name)}</h3>
        <div class="tournament-card-badges">
          <span class="tournament-status ${statusClass}" data-status="${tournament.status}">${statusText}</span>
          ${isPubliclyShared ? `<a href="/share/${tournament.shareToken}" class="public-badge" title="${t('tournament.publiclyShared', 'This tournament is publicly accessible')}" onclick="event.stopPropagation();">🌐 ${t('tournament.public', 'Public')}</a>` : ''}
          ${isShared ? `<span class="shared-badge" title="${t('tournament.sharedBy', 'Shared with you by')} ${escapeHtml(tournament.sharedBy || '')}">${t('tournament.sharedBy', 'Shared by')} ${escapeHtml(tournament.sharedBy || '')}</span>` : ''}
        </div>
      </div>

      <div class="tournament-card-body">
        <p class="tournament-info">
          <strong>${t('tournament.format.label', 'Format')}:</strong> ${formatDisplay}<br>
          ${tournament.timeControl ? `<strong>${t('tournament.timeControl', 'Time Control')}:</strong> ${escapeHtml(tournament.timeControl)}<br>` : ''}
          ${dateDisplay ? `<strong>${t('tournament.date', 'Date')}:</strong> ${escapeHtml(dateDisplay)}<br>` : ''}
          ${tournament.venue ? `<strong>${t('tournament.venue', 'Venue')}:</strong> ${escapeHtml(simplifyVenueName(tournament.venue))}<br>` : ''}
          ${tournament.numberOfRounds ? `<strong>${t('tournament.rounds', 'Rounds')}:</strong> ${tournament.numberOfRounds}<br>` : ''}
          <strong>${t('tournament.players', 'Players')}:</strong> ${playersCount}
        </p>
      </div>

      <div class="tournament-card-actions" onclick="event.stopPropagation();" style="display: flex; align-items: center; gap: 8px;">
        <button class="btn btn-primary" onclick="window.location.href='/tournament.html?id=${tournament.id}'">${t('tournament.manageTournament', 'Manage Tournament')}</button>
        <div style="display: flex; gap: 4px; margin-left: auto;">
          <a href="/api/tournaments/${tournament.id}/export.trf" download class="btn-icon" title="${t('tournament.exportTRFDescription', 'Download tournament data in FIDE TRF format')}" style="font-size: 0.65rem; text-decoration: none; display: inline-flex; align-items: center; justify-content: center; height: 28px; width: 32px;">.trf</a>
          ${isOwner ? `<button class="btn-icon btn-icon-danger" onclick="deleteTournament('${tournament.id}')" title="${t('tournament.delete', 'Delete')}" style="height: 28px; width: 32px; display: inline-flex; align-items: center; justify-content: center;">🗑</button>` : ''}
        </div>
      </div>
    </div>
  `;
}

/**
 * Create a complete tournament card with all tabs and UI elements
 * @param tournament - Tournament object with players, matches, etc.
 * @returns HTML string for tournament card
 */
export function createTournamentCard(tournament: Tournament): string {
  // Defensive: ensure players and matches arrays exist
  if (!tournament.players) {
    console.error('[createTournamentCard] Tournament missing players array:', tournament);
    tournament.players = [];
  }
  if (!tournament.matches) {
    console.error('[createTournamentCard] Tournament missing matches array:', tournament);
    tournament.matches = [];
  }

  const statusClass = `status-${tournament.status}`;
  const playersCount = tournament.players.length;
  const matchesCount = tournament.matches.length;
  const savedFormMode = getSavedPlayerFormMode();

  // Calculate the highest round number from matches
  const highestRound = matchesCount > 0
    ? Math.max(...tournament.matches.map(m => m.round))
    : 0;

  // Show owner controls if: tournament has no userId (legacy/unauthenticated) OR user is the owner (has userId and not shared)
  const isOwner = !tournament.userId || (tournament.userId && !tournament.sharedBy);
  const isShared = !!tournament.sharedBy;

  // Translate format labels
  const formatKey = tournament.format
    ? `tournament.format.${tournament.format.split('-').map((part, i) => i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)).join('')}`
    : 'tournament.format.label';
  const formatLabel = tournament.format
    ? t(formatKey, tournament.format.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' '))
    : t('tournament.format.label', 'Format');
  const formatDisplay = escapeHtml(formatLabel);

  // Format schedule with weekday and regional context
  let dateDisplay: string;
  if (tournament.startDate) {
    const startFormatted = formatRoundDateTime(tournament.startDate);
    if (tournament.endDate && tournament.endDate !== tournament.startDate) {
      const endFormatted = formatRoundDateTime(tournament.endDate);
      dateDisplay = `${startFormatted} - ${endFormatted}`;
    } else {
      dateDisplay = startFormatted;
    }
  } else {
    dateDisplay = t('tournament.scheduleTBD', 'Schedule TBD');
  }

  const roundStatValue = tournament.format === TOURNAMENT_FORMATS.SWISS && tournament.numberOfRounds
    ? `${highestRound}/${tournament.numberOfRounds}`
    : highestRound > 0 ? highestRound.toString() : '—';
  const roundStatLabel = (tournament.format === TOURNAMENT_FORMATS.SWISS && tournament.numberOfRounds)
    ? t('tournament.rounds', 'Rounds')
    : t('tournament.currentRound', 'Current Round');

  const matchesLabel = matchesCount === 1
    ? t('tournament.matchLogged', 'Match Logged')
    : t('tournament.matchesLogged', 'Matches Logged');

  // Translate status
  const statusKey = `status.${tournament.status}`;
  const statusText = t(statusKey, tournament.status);

  return `
    <div class="tournament-card" data-tournament-id="${tournament.id}">
      <div class="tournament-header">
        <div style="display: flex; align-items: center; gap: 16px; flex-wrap: wrap;">
          <div class="overview-logo-container">
            <img
              id="tournament-logo-${tournament.id}"
              src="${tournament.logo || DEFAULTS.TOURNAMENT_LOGO}"
              alt="Tournament Logo"
              class="tournament-logo-new"
              onclick="changeTournamentLogo('${tournament.id}')"
              title="${t('tournament.clickToChangeLogo', 'Click to change logo')}"
            />
            ${!tournament.logo ? `<div class="logo-change-hint">${t('tournament.clickToAddLogo', 'Click to add logo')}</div>` : ''}
            <input
              type="file"
              id="logo-input-${tournament.id}"
              accept="image/*"
              style="display: none;"
              onchange="handleLogoUpload('${tournament.id}', this)"
            />
          </div>
          <h3 class="tournament-title" id="tournament-title-${tournament.id}" style="margin: 0; display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
            <span class="editable-title" onclick="editField('${tournament.id}', 'name', '${escapeHtml(tournament.name)}', 'text', undefined, true)" style="cursor: pointer; position: relative; padding: 2px 4px; border-radius: 4px; transition: background 0.2s;" onmouseover="this.style.background='rgba(0,0,0,0.05)'" onmouseout="this.style.background='transparent'">
              ${escapeHtml(tournament.name)}
              <span style="font-size: 0.7em; opacity: 0.5; margin-left: 4px;">✎</span>
            </span>
            <div class="status-dropdown-container" style="position: relative; display: inline-block;">
              <span class="tournament-status ${statusClass} status-clickable" data-status="${tournament.status}" data-tournament-id="${tournament.id}" onclick="openStatusDropdown('${tournament.id}', event)">${statusText}</span>
              <div id="status-dropdown-${tournament.id}" class="status-dropdown" style="display: none;">
                <div class="status-dropdown-option ${tournament.status === 'upcoming' ? 'active' : ''}" onclick="changeStatus('${tournament.id}', 'upcoming', event)">${t('status.upcoming', 'Upcoming')}</div>
                <div class="status-dropdown-option ${tournament.status === 'ongoing' ? 'active' : ''}" onclick="changeStatus('${tournament.id}', 'ongoing', event)">${t('status.ongoing', 'Ongoing')}</div>
                <div class="status-dropdown-option ${tournament.status === 'completed' ? 'active' : ''}" onclick="changeStatus('${tournament.id}', 'completed', event)">${t('status.completed', 'Completed')}</div>
              </div>
            </div>
            ${isShared ? `<span class="shared-badge" title="${t('tournament.sharedBy', 'Shared with you by')} ${escapeHtml(tournament.sharedBy || '')}">${t('tournament.sharedBy', 'Shared by')} ${escapeHtml(tournament.sharedBy || '')}</span>` : ''}
          </h3>
          ${''}${/* SUSPENDED: Tier system hidden until app reaches popularity threshold
          playersCount > 20 && (tournament.subscriptionTier === 'free' || !tournament.subscriptionTier) ? `
            <button onclick="window.location.href='/billing'" class="upgrade-badge" title="${t('upgrade.unlockPro', 'Unlock Pro features')}" style="padding: 4px 10px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border-radius: 4px; font-size: 12px; font-weight: 600; text-decoration: none; display: inline-block; white-space: nowrap; pointer-events: auto; border: none; cursor: pointer;">
              ✨ ${t('upgrade.upgradePro', 'Upgrade to Pro')}
            </button>
          ` : '' */ ''}
        </div>
      </div>

      <div id="tournament-content-${tournament.id}" class="main-tournament-tabs">
        <div class="main-tab-buttons">
          <button class="main-tab-button active" data-tab="overview-${tournament.id}" onclick="switchMainTab('${tournament.id}', 'overview')">${t('tournament.overview', 'Overview')}</button>
          <button class="main-tab-button" data-tab="players-${tournament.id}" onclick="switchMainTab('${tournament.id}', 'players')">${t('tournament.players', 'Players')}</button>
          ${(highestRound > 0 || tournament.format === TOURNAMENT_FORMATS.SWISS || tournament.format === TOURNAMENT_FORMATS.ROUND_ROBIN || tournament.format === TOURNAMENT_FORMATS.KNOCKOUT) ? `
            <button class="main-tab-button" data-tab="pairings-${tournament.id}" onclick="switchMainTab('${tournament.id}', 'pairings')">${t('pairings.title', 'Pairings')}</button>
            <button class="main-tab-button" data-tab="standings-${tournament.id}" onclick="switchMainTab('${tournament.id}', 'standings')">${t('standings.title', 'Standings')}</button>
          ` : ''}
        </div>

        <div class="main-tab-content">
          <!-- Overview Tab -->
          <div id="overview-${tournament.id}" class="main-tab-pane active">
            <div class="tournament-overview-header">
              <div class="overview-panel-new">
                <!-- Tournament Information Section -->
                <div class="feature-section">
                  <div class="feature-section-header" onclick="toggleFeatureSection('${tournament.id}', 'keyinfo', event)">
                    <h4 class="feature-section-title">${t('tournament.keyInfo', 'Key Information')}</h4>
                    <span id="keyinfo-icon-${tournament.id}" class="feature-toggle-arrow">▶</span>
                  </div>
                  <div id="keyinfo-section-${tournament.id}" class="feature-section-content" style="display: none;">

                  <!-- Announcement -->
                  <div style="margin-bottom: 16px;">
                    <label class="detail-label" style="display: block; margin-bottom: 8px;">${t('tournament.announcement', 'Announcement')}</label>
                    <div class="description-content editable" onclick="editDescription('${tournament.id}', event)" style="cursor: pointer; position: relative;">
                      ${tournament.description ? `
                        <div id="description-wrapper-${tournament.id}" class="description-wrapper">
                          <div class="description-text">${tournament.description}</div>
                        </div>
                        <button id="description-toggle-${tournament.id}" class="description-toggle" onclick="event.stopPropagation(); toggleDescription('${tournament.id}')">${t('tournament.readMore', 'Read more')}</button>
                      ` : `
                        <div class="description-text empty">${t('tournament.addAnnouncement', 'Click to add an announcement...')}</div>
                      `}
                      <span class="edit-icon-small" style="position: absolute; top: 8px; right: 8px;">✎</span>
                    </div>
                  </div>

                  <div class="detail-grid-new">
                    <div class="detail-row">
                      <span class="detail-label">${t('tournament.dates', 'Dates')}</span>
                      <div class="detail-value editable" onclick="editDateRange('${tournament.id}', '${tournament.startDate ? new Date(tournament.startDate).toISOString().split('T')[0] : ''}', '${tournament.endDate ? new Date(tournament.endDate).toISOString().split('T')[0] : ''}')" style="cursor: pointer;">
                        <span>${tournament.startDate || tournament.endDate ? escapeHtml(dateDisplay) : '<span class="value-empty">' + t('tournament.addDates', 'Click to set dates...') + '</span>'}</span>
                        <span class="edit-icon-small">✎</span>
                        ${(() => {
                          // Calculate expected number of rounds based on format
                          let expectedRounds = 0;
                          let canShowButton = false;
                          let isEnabled = false;

                          if (tournament.format === TOURNAMENT_FORMATS.SWISS && tournament.numberOfRounds) {
                            expectedRounds = tournament.numberOfRounds;
                            canShowButton = true;
                            isEnabled = true; // Swiss can always edit once numberOfRounds is set
                          } else if (tournament.format === TOURNAMENT_FORMATS.ROUND_ROBIN) {
                            // Round-robin: (n-1) rounds for n players, or n rounds if odd
                            // Double if doubleRoundRobin is enabled
                            const n = playersCount;
                            const baseRounds = n % 2 === 0 ? n - 1 : n;
                            expectedRounds = tournament.doubleRoundRobin ? baseRounds * 2 : baseRounds;
                            canShowButton = playersCount >= 2;
                            isEnabled = highestRound > 0; // Only enable once rounds are generated
                          } else if (tournament.format === TOURNAMENT_FORMATS.KNOCKOUT) {
                            // Knockout: log2(n) rounds
                            expectedRounds = playersCount <= 2 ? 1 : Math.ceil(Math.log2(playersCount));
                            canShowButton = playersCount >= 2;
                            isEnabled = highestRound > 0; // Only enable once rounds are generated
                          }

                          if (!canShowButton) return '';

                          const disabledAttr = isEnabled ? '' : 'disabled';
                          const disabledStyle = isEnabled ? '' : 'opacity: 0.5; cursor: not-allowed;';
                          const tooltip = isEnabled ? '' : `title="${t('tournament.generateRoundsFirst', 'Generate rounds first')}"`;

                          return `
                            <button class="btn btn-secondary btn-small" ${disabledAttr} ${tooltip} onclick="event.stopPropagation(); ${isEnabled ? `openScheduleModal('${tournament.id}', '${tournament.format}', ${expectedRounds})` : ''}" style="margin-left: auto; ${disabledStyle}">
                              ${t('tournament.editRoundDates', 'Edit Round Dates')}
                            </button>
                          `;
                        })()}
                      </div>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">${t('tournament.venue', 'Location')}</span>
                      <div class="detail-value editable" onclick="editField('${tournament.id}', 'venue', '${escapeHtml(tournament.venue || '')}', 'text', undefined, true)">
                        <span>${tournament.venue ? escapeHtml(simplifyVenueName(tournament.venue)) : '<span class="value-empty">' + t('tournament.addVenue', 'Click to add location...') + '</span>'}</span>
                        <span class="edit-icon-small">✎</span>
                      </div>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">${t('tournament.isRated', 'Rated')}</span>
                      <div class="detail-value">
                        <div class="toggle-switch ${tournament.isRated ? 'active' : ''}" id="rated-toggle-${tournament.id}" onclick="toggleRated('${tournament.id}', event)">
                          <div class="toggle-slider"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  ${tournament.latitude && tournament.longitude ? `
                  <div style="margin-top: 16px;">
                    <div id="map-${tournament.id}" style="width: 100%; height: 200px; border-radius: 8px; border: 1px solid #e5e7eb;"></div>
                  </div>
                  ` : ''}
                  </div>
                </div>

                <!-- Tournament Settings Section -->
                <div class="feature-section">
                  <div class="feature-section-header" onclick="toggleFeatureSection('${tournament.id}', 'settings', event)">
                    <h4 class="feature-section-title">${t('tournament.settings', 'Tournament Settings')}</h4>
                    <span id="settings-icon-${tournament.id}" class="feature-toggle-arrow">▶</span>
                  </div>
                  <div id="settings-section-${tournament.id}" class="feature-section-content" style="display: none;">

                  <!-- Settings Grid -->
                  <div class="detail-grid-new">
                    <div class="detail-row">
                      <span class="detail-label">${t('tournament.format.label', 'Format')}</span>
                      <div class="detail-value">
                        <div style="display: flex; align-items: center; gap: 12px;">
                          <div id="format-control-${tournament.id}" class="segmented-control">
                            <button class="segment-button ${tournament.format === 'swiss' ? 'active' : ''}" data-value="swiss" onclick="setFormat('${tournament.id}', 'swiss', event)" ${highestRound > 0 ? 'disabled' : ''}>${t('tournament.format.swiss', 'Swiss')}</button>
                            <button class="segment-button ${tournament.format === 'round-robin' ? 'active' : ''}" data-value="round-robin" onclick="setFormat('${tournament.id}', 'round-robin', event)" ${highestRound > 0 ? 'disabled' : ''}>${t('tournament.format.roundRobin', 'Round Robin')}</button>
                            <button class="segment-button ${tournament.format === 'knockout' ? 'active' : ''}" data-value="knockout" onclick="setFormat('${tournament.id}', 'knockout', event)" ${highestRound > 0 ? 'disabled' : ''}>${t('tournament.format.knockout', 'Knockout')}</button>
                          </div>
                          ${highestRound > 0 ? `<span style="font-size: 0.85em; color: #6b7280; font-style: italic;">${t('tournament.formatLockedAfterRounds', 'Format cannot be changed after rounds have been generated')}</span>` : ''}
                        </div>
                      </div>
                    </div>
                    ${tournament.format === TOURNAMENT_FORMATS.SWISS ? `
                    <div class="detail-row">
                      <span class="detail-label">${t('tournament.numberOfRounds', 'Number of Rounds')}</span>
                      <div class="detail-value editable" data-current-round="${highestRound}" onclick="editField('${tournament.id}', 'numberOfRounds', '${tournament.numberOfRounds || ''}', 'text', undefined, true)">
                        <span>${tournament.numberOfRounds || '<span class="value-empty">' + t('tournament.notSet', 'Not set') + '</span>'}</span>
                        <span class="edit-icon-small">✎</span>
                      </div>
                    </div>
                    ` : ''}
                    <div class="detail-row">
                      <span class="detail-label">${t('tournament.timeControl', 'Time Control')}</span>
                      <div class="detail-value">
                        <div id="time-control-${tournament.id}" class="segmented-control">
                          <button class="segment-button ${(tournament.timeControl || 'blitz') === 'blitz' ? 'active' : ''}" data-value="blitz" onclick="setTimeControl('${tournament.id}', 'blitz', event)">${t('tournament.timeControlBlitz', 'Blitz')}</button>
                          <button class="segment-button ${(tournament.timeControl || 'blitz') === 'rapid' ? 'active' : ''}" data-value="rapid" onclick="setTimeControl('${tournament.id}', 'rapid', event)">${t('tournament.timeControlRapid', 'Rapid')}</button>
                          <button class="segment-button ${(tournament.timeControl || 'blitz') === 'classical' ? 'active' : ''}" data-value="classical" onclick="setTimeControl('${tournament.id}', 'classical', event)">${t('tournament.timeControlClassical', 'Classical')}</button>
                        </div>
                      </div>
                    </div>
                    ${tournament.format !== 'knockout' ? `
                    <div class="detail-row">
                      <span class="detail-label">${t('tournament.initialColor.label', '1st round color of best ranked player')}</span>
                      <div class="detail-value">
                        <div style="display: flex; align-items: center; gap: 12px;">
                          <div id="initial-color-control-${tournament.id}" class="segmented-control">
                            <button class="segment-button ${(tournament.initialColor || 'white') === 'white' ? 'active' : ''}" data-value="white" onclick="setInitialColor('${tournament.id}', 'white', event)" ${highestRound > 0 ? 'disabled' : ''}>${t('tournament.initialColor.white', 'White')}</button>
                            <button class="segment-button ${(tournament.initialColor || 'white') === 'black' ? 'active' : ''}" data-value="black" onclick="setInitialColor('${tournament.id}', 'black', event)" ${highestRound > 0 ? 'disabled' : ''}>${t('tournament.initialColor.black', 'Black')}</button>
                            <button class="segment-button ${(tournament.initialColor || 'white') === 'random' ? 'active' : ''}" data-value="random" onclick="setInitialColor('${tournament.id}', 'random', event)" ${highestRound > 0 ? 'disabled' : ''}>${t('tournament.initialColor.random', 'Random')}</button>
                          </div>
                          ${highestRound > 0 ? `<span style="font-size: 0.85em; color: #6b7280; font-style: italic;">${t('tournament.initialColorLockedAfterRounds', 'Initial color cannot be changed after rounds have been generated')}</span>` : ''}
                        </div>
                      </div>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">${t('tournament.tiebreakCriteria', 'Tiebreak Order')}</span>
                      <div class="detail-value">
                        <div id="tiebreak-list-${tournament.id}" class="tiebreak-sortable-list" data-tournament-id="${tournament.id}">
                          ${(() => {
                            const format = tournament.format as 'swiss' | 'round-robin' | 'knockout';
                            const defaultCriteria = DEFAULT_TIEBREAKS_BY_FORMAT[format] || DEFAULT_TIEBREAKS_BY_FORMAT['swiss'];
                            const criteria = tournament.tiebreakCriteria || defaultCriteria;
                            const enabledSet = new Set(criteria);

                            // Get recommended tiebreaks for this format
                            const recommendedKeys = ALL_TIEBREAK_KEYS.filter(key => {
                              const def = TIEBREAK_DEFINITIONS[key];
                              return def && def.recommendedFor.includes(format);
                            });

                            // Other available tiebreaks (not recommended but available)
                            const otherKeys = ALL_TIEBREAK_KEYS.filter(key => {
                              const def = TIEBREAK_DEFINITIONS[key];
                              return def && !def.recommendedFor.includes(format) && def.recommendedFor.length > 0;
                            });

                            // Order: enabled first (in their order), then recommended disabled, then others
                            const enabledCriteria = criteria.filter((c: TiebreakCriterion) => ALL_TIEBREAK_KEYS.includes(c));
                            const disabledRecommended = recommendedKeys.filter(k => !enabledSet.has(k));
                            const disabledOther = otherKeys.filter(k => !enabledSet.has(k));
                            const orderedCriteria = [...enabledCriteria, ...disabledRecommended, ...disabledOther];

                            return orderedCriteria.map((criterion: TiebreakCriterion) => {
                              const isEnabled = enabledSet.has(criterion);
                              const def = TIEBREAK_DEFINITIONS[criterion];
                              const label = def ? t(def.nameKey, criterion) : criterion;
                              const isRecommended = def?.recommendedFor.includes(format);
                              const atMaxEnabled = enabledSet.size >= MAX_TIEBREAK_CRITERIA && !isEnabled;
                              const enabledClass = isEnabled ? 'enabled' : 'disabled';
                              const recommendedClass = !isRecommended ? 'not-recommended' : '';
                              const activeClass = isEnabled ? 'active' : '';
                              const buttonTitle = atMaxEnabled
                                ? t('tournament.tiebreak.maxReached', 'Maximum 5 criteria')
                                : (isEnabled ? t('tournament.tiebreak.disable', 'Disable') : t('tournament.tiebreak.enable', 'Enable'));
                              const disabledAttr = atMaxEnabled ? 'disabled' : '';
                              const checkmark = isEnabled ? '✓' : '○';

                              const onclickHandler = "toggleTiebreakCriterion('" + tournament.id + "', '" + criterion + "', event)";
                              return '<div class="tiebreak-item ' + enabledClass + ' ' + recommendedClass + '" data-criterion="' + criterion + '" draggable="true">' +
                                '<span class="tiebreak-drag-handle">≡</span>' +
                                '<span class="tiebreak-label">' + label + '</span>' +
                                '<button class="tiebreak-toggle ' + activeClass + '" onclick="' + onclickHandler + '" title="' + buttonTitle + '" ' + disabledAttr + '>' +
                                checkmark +
                                '</button>' +
                                '</div>';
                            }).join('');
                          })()}
                        </div>
                        <small style="display: block; margin-top: 8px; color: #6b7280;">${t('tournament.tiebreakHint', 'Drag to reorder. Max 5 criteria. Score is always first.')}</small>
                      </div>
                    </div>
                    ` : ''}

                    <!-- Player Categories (inside Tournament Settings) -->
                    <div class="detail-row" style="flex-direction: column; align-items: flex-start; gap: 8px;">
                      <span class="detail-label">${t('category.title', 'Player Categories')}</span>
                      <div class="detail-value" style="width: 100%;">
                        <div id="categories-content-${tournament.id}">
                          <p class="loading-text">${t('common.loading', 'Loading...')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>

                ${isOwner ? `
                <!-- Publishing & Sharing Section -->
                <div class="feature-section">
                  <div class="feature-section-header" onclick="toggleFeatureSection('${tournament.id}', 'publishing', event)">
                    <h4 class="feature-section-title">${t('tournament.publishingSharing', 'Publishing & Sharing')}</h4>
                    <span id="publishing-icon-${tournament.id}" class="feature-toggle-arrow">▶</span>
                  </div>
                  <div id="publishing-section-${tournament.id}" class="feature-section-content" style="display: none;">
                    <!-- Sharing -->
                    <div class="feature-subsection">
                      <h5 class="feature-subsection-title">${t('tournament.sharing', 'Sharing')}</h5>

                      <label class="feature-label">
                        <input type="checkbox"
                          class="toggle-switch-input"
                          id="enable-share-link-${tournament.id}"
                          ${tournament.shareToken ? 'checked' : ''}
                          onchange="toggleShareLinkVisibility('${tournament.id}', this.checked)">
                        <span class="toggle-switch-slider"></span>
                        <span class="toggle-switch-label">${t('tournament.enableShareLink', 'Enable shareable link')}</span>
                      </label>

                      <div id="share-link-section-${tournament.id}" class="share-link-section" style="display: ${tournament.shareToken ? 'block' : 'none'};">
                        <div class="share-link-display">
                          <input type="text" readonly value="${window.location.origin}/share/${tournament.shareToken || 'generating...'}" class="share-link-input" id="share-link-input-${tournament.id}">
                          <button class="btn-copy" onclick="copyPublishingShareLink('${tournament.id}', event)">${t('common.copy', 'Copy')}</button>
                        </div>
                        <div class="share-link-actions">
                          <button class="btn-secondary btn-small" onclick="window.open('${window.location.origin}/share/${tournament.shareToken}', '_blank')" id="view-tournament-btn-${tournament.id}">${t('tournament.viewTournament', 'View Tournament')}</button>
                          <button class="btn-secondary btn-small" onclick="showEmbedCode('${tournament.id}')">${t('tournament.embedCode', 'Embed Code')}</button>
                        </div>
                      </div>

                      <label class="feature-label" style="margin-top: 12px;">
                        <input type="checkbox"
                          class="toggle-switch-input"
                          id="publish-directory-${tournament.id}"
                          ${tournament.published ? 'checked' : ''}
                          ${!tournament.venue ? 'disabled' : ''}
                          onchange="togglePublicDirectory('${tournament.id}', this.checked)">
                        <span class="toggle-switch-slider"></span>
                        <span class="toggle-switch-label">${t('tournament.publishDirectory', 'Publish to public directory')}</span>
                      </label>
                      ${!tournament.venue ? `
                      <div style="margin: 4px 0 0 24px; font-size: 0.85em; color: #764ba2;">
                        ${t('tournament.venueRequired', 'Venue required.')}
                        <a href="#" onclick="event.preventDefault(); event.stopPropagation(); scrollToVenueAndEdit('${tournament.id}');" style="color: #667eea; text-decoration: underline; cursor: pointer;">
                          ${t('tournament.addLocationLink', 'Add a location')}
                        </a>
                      </div>
                      ` : ''}

                      ${tournament.published ? `
                      <div style="margin: 8px 0 0 24px;">
                        <button class="btn-secondary btn-small" onclick="window.open('/tournaments.html', '_blank')">${t('tournament.viewInDirectory', 'View in Public Directory')}</button>
                      </div>
                      ` : ''}

                      <label class="feature-label" style="margin-top: 12px; display: ${tournament.shareToken || tournament.published ? 'flex' : 'none'};">
                        <input type="checkbox"
                          class="toggle-switch-input"
                          id="allow-self-reg-${tournament.id}"
                          ${tournament.allowSelfRegistration ? 'checked' : ''}
                          onchange="toggleSelfRegistration('${tournament.id}', this.checked)">
                        <span class="toggle-switch-slider"></span>
                        <span class="toggle-switch-label">${t('tournament.allowSelfRegistration', 'Allow viewers to self-register as players')}</span>
                      </label>

                      <label class="feature-label" style="margin-top: 12px; display: ${tournament.shareToken || tournament.published ? 'flex' : 'none'};">
                        <input type="checkbox"
                          class="toggle-switch-input"
                          id="allow-player-results-${tournament.id}"
                          ${tournament.allowUserResultEntry ? 'checked' : ''}
                          onchange="togglePlayerResultEntry('${tournament.id}', this.checked)">
                        <span class="toggle-switch-slider"></span>
                        <span class="toggle-switch-label">${t('tournament.allowPlayerResultEntry', 'Allow players to enter their own results')}</span>
                      </label>
                    </div>

                    <!-- Collaboration -->
                    <div class="feature-subsection">
                      <h5 class="feature-subsection-title">${t('tournament.collaboration', 'Collaboration')}</h5>
                      <button class="btn-primary btn-small" onclick="openShareModal('${tournament.id}')">
                        ${t('tournament.inviteUser', 'Invite User to Edit')}
                      </button>
                    </div>

                    <!-- Export -->
                    <div class="feature-subsection">
                      <h5 class="feature-subsection-title">${t('tournament.export', 'Export')}</h5>
                      <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                        <a href="/api/tournaments/${tournament.id}/export.trf" download class="btn-secondary btn-small" title="${t('tournament.exportTRFDescription', 'Download tournament data in FIDE TRF format')}">
                          ${t('tournament.exportTRF', 'Export TRF')}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Entry Fees Section -->
                <div class="feature-section">
                  <div class="feature-section-header" onclick="toggleFeatureSection('${tournament.id}', 'fees', event)">
                    <h4 class="feature-section-title">${t('tournament.entryFees', 'Entry Fees')}</h4>
                    <span id="fees-icon-${tournament.id}" class="feature-toggle-arrow">▶</span>
                  </div>
                  <div id="fees-section-${tournament.id}" class="feature-section-content" style="display: none;">
                    <div class="feature-row">
                      <label class="feature-label">
                        <input type="checkbox"
                          class="toggle-switch-input"
                          id="has-fees-${tournament.id}"
                          ${tournament.hasEntryFees ? 'checked' : ''}
                          onchange="toggleEntryFees('${tournament.id}', this.checked)">
                        <span class="toggle-switch-slider"></span>
                        <span class="toggle-switch-label">${t('tournament.hasEntryFees', 'Tournament has entry fees')}</span>
                      </label>
                    </div>

                    <div id="fee-options-${tournament.id}" style="display: ${tournament.hasEntryFees ? 'block' : 'none'};">
                      <div class="feature-row" style="display: flex; gap: 8px; flex-wrap: wrap;">
                        <button class="btn-secondary btn-small" onclick="openEntryFeesModal('${tournament.id}')">${t('tournament.manageFeeStructure', 'Manage Fee Structure')}</button>
                        <button class="btn-secondary btn-small" onclick="openPaymentSummaryModalFor('${tournament.id}')">${t('tournament.paymentSummary', 'Payment Summary')}</button>
                      </div>

                      <div class="detail-row">
                        <span class="detail-label">${t('tournament.paymentMode', 'Payment method')}</span>
                        <div class="detail-value">
                          <div id="payment-mode-${tournament.id}" class="segmented-control">
                            <button class="segment-button ${!tournament.stripeConnectedAccountId ? 'active' : ''}" data-value="onsite" onclick="setPaymentMode('${tournament.id}', 'onsite', event)">${t('tournament.paymentModeOnsite', 'Cash')}</button>
                            <button class="segment-button ${tournament.stripeConnectedAccountId && !tournament.allowOnsitePayment ? 'active' : ''}" data-value="stripe" onclick="setPaymentMode('${tournament.id}', 'stripe', event)">${t('tournament.paymentModeStripe', 'Stripe')}</button>
                            <button class="segment-button ${tournament.stripeConnectedAccountId && tournament.allowOnsitePayment ? 'active' : ''}" data-value="both" onclick="setPaymentMode('${tournament.id}', 'both', event)">${t('tournament.paymentModeBoth', 'Both')}</button>
                          </div>
                        </div>
                      </div>

                      <div id="stripe-options-${tournament.id}" style="display: ${tournament.stripeConnectedAccountId ? 'block' : 'none'};">
                        <div id="stripe-connect-container-${tournament.id}" class="stripe-connect-container">
                          <!-- Stripe Connect UI will be loaded dynamically -->
                          <div class="loading">${t('common.loading', 'Loading...')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                ` : ''}
              </div>
            ${tournament.shareToken ? `
              <div id="share-info-${tournament.id}" class="share-info" style="display: none;">
                <p><strong>${t('share.publicShareLink', 'Public Share Link:')}</strong></p>
                <div class="share-link-container">
                  <input type="text" id="share-url-${tournament.id}" value="${window.location.origin}/share/${tournament.shareToken}?lang=${window.i18n?.getCurrentLanguage() || localStorage.getItem('language') || 'en'}" readonly>
                  <button class="btn btn-small btn-secondary" onclick="copyShareLink('${tournament.id}', event)">${t('share.copyButton', 'Copy')}</button>
                  <button class="btn btn-small btn-primary" onclick="window.open('${window.location.origin}/share/${tournament.shareToken}?lang=${window.i18n?.getCurrentLanguage() || localStorage.getItem('language') || 'en'}', '_blank')">${t('share.goToButton', 'Go to')}</button>
                </div>

                <p style="margin-top: 16px;"><strong>Embed Code (for websites):</strong></p>
                <div class="share-link-container">
                  <textarea id="embed-code-${tournament.id}" readonly style="min-height: 80px; font-family: monospace; font-size: 12px; resize: vertical;"><iframe src="${window.location.origin}/embed/${tournament.shareToken}?lang=${window.i18n?.getCurrentLanguage() || localStorage.getItem('language') || 'en'}" width="100%" height="600" frameborder="0" style="border: 1px solid #ddd; border-radius: 4px;"></iframe></textarea>
                  <button class="btn btn-small btn-secondary" onclick="copyEmbedCode('${tournament.id}', event)">Copy</button>
                  <button class="btn btn-small btn-primary" onclick="window.open('${window.location.origin}/embed/${tournament.shareToken}?lang=${window.i18n?.getCurrentLanguage() || localStorage.getItem('language') || 'en'}', '_blank')">Preview</button>
                </div>
                <p style="font-size: 0.85em; color: #666; margin-top: 8px;">
                  Copy this code and paste it into your website's HTML to embed the tournament.
                  You can adjust the width and height attributes to fit your needs.
                </p>
              </div>
            ` : ''}
            </div>
          </div>

          <!-- Players Tab -->
          <div id="players-tab-${tournament.id}" class="main-tab-pane">
            <div class="players-list">
              <!-- Pending registrations will be injected here -->
              <div id="pending-registrations-${tournament.id}"></div>

              <div class="player-filter-bar">
                <button id="add-player-btn-${tournament.id}" class="btn-icon btn-add-player" onclick="toggleAddPlayerForm('${tournament.id}')" title="${t('player.addPlayer', 'Add player')}">+</button>
                ${playersCount > 0 ? `
                  <input type="text"
                    id="player-filter-${tournament.id}"
                    class="player-filter-input"
                    placeholder="${t('player.filterPlayers', 'Filter players...')}"
                    oninput="filterPlayers('${tournament.id}', this.value)"
                  >
                  ${highestRound === 0 ? `
                    <select id="player-present-filter-${tournament.id}" class="player-sort-select" onchange="filterPlayersByPresent('${tournament.id}', this.value)">
                      <option value="all">${t('player.showAll', 'Show all')}</option>
                      <option value="present">${t('player.showPresent', 'Present only')}</option>
                      <option value="notPresent">${t('player.showNotPresent', 'Not present')}</option>
                    </select>
                  ` : ''}
                  <select id="player-sort-${tournament.id}" class="player-sort-select" onchange="sortPlayers('${tournament.id}', this.value)">
                    <option value="initialRanking" ${getSavedPlayerSortOrder() === 'initialRanking' ? 'selected' : ''}>${t('player.sortByRank', 'Sort by rank')}</option>
                    <option value="firstName" ${getSavedPlayerSortOrder() === 'firstName' ? 'selected' : ''}>${t('player.sortByFirstName', 'Sort by first name')}</option>
                    <option value="lastName" ${getSavedPlayerSortOrder() === 'lastName' ? 'selected' : ''}>${t('player.sortByLastName', 'Sort by last name')}</option>
                  </select>
                  <span class="player-count">${playersCount} ${playersCount === 1 ? t('player.player', 'player') : t('player.players', 'players')}</span>
                ` : ''}
              </div>
              <div id="player-category-filter-${tournament.id}" class="player-category-filter"></div>

              <div id="player-form-${tournament.id}" class="player-form" data-mode="${savedFormMode}" style="display: ${playersCount === 0 ? 'block' : 'none'};">
                <h4>${t('tournament.addNewPlayer', 'Add New Player')}</h4>
                ${''}${/* SUSPENDED: Tier system hidden until app reaches popularity threshold
                playersCount >= 20 && (tournament.subscriptionTier === 'free' || !tournament.subscriptionTier) ? `
                  <p style="margin: 8px 0 12px 0; font-size: 14px; color: #666;">
                    ${t('upgrade.limitReached', 'The 20 player limit of the free plan has been reached.')}
                    <a href="#" onclick="event.preventDefault(); window.location.href='/billing';" style="color: #667eea; font-weight: 600; text-decoration: underline; cursor: pointer;">${t('upgrade.upgradePro', 'Upgrade to Pro')} →</a>
                  </p>
                ` : '' */ ''}
                <div class="player-input-group">
                  <div class="autocomplete-wrapper">
                    <input type="text" id="player-search-${tournament.id}" placeholder="${t('tournament.searchPlayer', 'Search player...')}" autocomplete="off">
                    <div id="player-suggestions-${tournament.id}" class="player-suggestions" style="display: none;"></div>
                  </div>
                  <input type="text" id="player-first-name-${tournament.id}" placeholder="${t('tournament.firstName', 'First Name')}" required class="player-field-simple">
                  <div class="field-autocomplete-wrapper">
                    <input type="text" id="player-last-name-${tournament.id}" placeholder="${t('tournament.lastName', 'Last Name')}" class="player-field-simple" autocomplete="off">
                    <div id="lastname-suggestions-${tournament.id}" class="field-autocomplete-dropdown" style="display: none;"></div>
                  </div>
                  <input type="number" id="player-rating-${tournament.id}" placeholder="${t('tournament.rating', 'Rating (optional)')}" class="player-field-simple">
                  <select id="player-title-${tournament.id}" class="input-small player-field-advanced">
                    <option value="">${t('tournament.noTitle', 'No Title')}</option>
                    <option value="GM">GM - ${t('tournament.grandmaster', 'Grandmaster')}</option>
                    <option value="IM">IM - ${t('tournament.internationalMaster', 'International Master')}</option>
                    <option value="FM">FM - ${t('tournament.fideMaster', 'FIDE Master')}</option>
                    <option value="CM">CM - ${t('tournament.candidateMaster', 'Candidate Master')}</option>
                    <option value="WGM">WGM - ${t('tournament.womanGrandmaster', 'Woman Grandmaster')}</option>
                    <option value="WIM">WIM - ${t('tournament.womanInternationalMaster', 'Woman International Master')}</option>
                    <option value="WFM">WFM - ${t('tournament.womanFideMaster', 'Woman FIDE Master')}</option>
                    <option value="WCM">WCM - ${t('tournament.womanCandidateMaster', 'Woman Candidate Master')}</option>
                    <option value="NM">NM - ${t('tournament.nationalMaster', 'National Master')}</option>
                  </select>
                  <input type="text" id="player-federation-${tournament.id}" placeholder="${t('tournament.federation', 'Federation (e.g., SUI)')}" class="input-small player-field-advanced" maxlength="3" style="text-transform: uppercase;">
                  <input type="text" id="player-club-${tournament.id}" placeholder="${t('tournament.club', 'Club (optional)')}" class="player-field-advanced">
                  <div class="field-autocomplete-wrapper">
                    <input type="text" id="player-fide-id-${tournament.id}" placeholder="${t('tournament.fideId', 'FIDE ID (optional)')}" class="input-medium player-field-advanced" autocomplete="off">
                    <div id="fideid-suggestions-${tournament.id}" class="field-autocomplete-dropdown" style="display: none;"></div>
                  </div>
                  <input type="text" id="player-federation-code-${tournament.id}" placeholder="${t('tournament.federationCode', 'Federation Code (optional)')}" class="input-medium player-field-advanced">
                  <input type="date" id="player-birth-date-${tournament.id}" class="input-medium player-field-advanced" title="${t('player.birthDateHint', 'Birth date for age categories')}">
                  <select id="player-gender-${tournament.id}" class="input-small player-field-advanced" title="${t('player.genderHint', 'Gender for category filtering')}">
                    <option value="">${t('player.noGender', 'Gender')}</option>
                    <option value="M">${t('player.male', 'Male')}</option>
                    <option value="F">${t('player.female', 'Female')}</option>
                  </select>
                  <div id="add-player-categories-${tournament.id}" class="player-categories-section player-field-advanced" style="display: none;"></div>
                  <button id="add-player-${tournament.id}" class="btn btn-primary">${t('tournament.add', 'Add')}</button>
                </div>
                <div class="player-form-footer">
                  <small class="player-search-hint">${t('tournament.searchHint', 'Search for a player or enter details manually...')}</small>
                  <a href="#" id="player-mode-toggle-${tournament.id}" class="player-mode-toggle" onclick="togglePlayerFormMode('${tournament.id}'); return false;">
                    <span class="toggle-text-simple">${t('player.showAdvanced', 'Show more fields')}</span>
                    <span class="toggle-text-advanced">${t('player.showSimple', 'Show fewer fields')}</span>
                  </a>
                </div>
              </div>

              ${playersCount > 0 ? `
                <ul id="player-list-ul-${tournament.id}">
                  ${getSortedPlayers(tournament.players).map((player, index) => `
                    <li id="player-${tournament.id}-${player.id}" class="player-row player-row-compact"
                      data-name="${escapeHtml((player.firstName + ' ' + player.lastName).toLowerCase())}"
                      data-firstname="${escapeHtml(player.firstName.toLowerCase())}"
                      data-lastname="${escapeHtml(player.lastName.toLowerCase())}"
                      data-initialranking="${player.initialRanking ?? index + 1}"
                      data-rating="${player.rating || ''}"
                      data-federation="${(player.federation || '').toLowerCase()}"
                      data-club="${(player.club || '').toLowerCase()}"
                      data-title="${(player.title || '').toLowerCase()}"
                      data-playerid="${player.id}"
                      data-present="${player.present ? 'true' : 'false'}">
                      <div class="player-row-main">
                        <span class="player-name">
                          ${player.title ? `<span class="player-title-badge">${escapeHtml(player.title)}</span>` : ''}${escapeHtml(player.firstName)} ${escapeHtml(player.lastName)}${player.rating ? ` <span class="player-rating-inline">${player.rating}</span>` : ''}${player.federation ? ` <span class="player-fed-inline">${escapeHtml(String(player.federation).toUpperCase())}</span>` : ''}${player.club ? ` <span class="player-club-inline">${escapeHtml(player.club)}</span>` : ''}${player.feePaid && player.paidAmount ? ` <span class="player-paid-badge${isOwner && player.paymentSource === 'manual' ? ' player-paid-badge-clearable' : ''}" ${isOwner && player.paymentSource === 'manual' ? `onclick="clearPlayerFeePaid('${tournament.id}', '${player.id}')" style="cursor: pointer;"` : ''} title="${t('player.paidFee', 'Paid')}: ${formatCurrency(player.paidAmount, player.paidCurrency || 'CHF')}${isOwner && player.paymentSource === 'manual' ? ' - ' + t('player.clickToClear', 'Click to clear') : ''}">${t('player.paid', 'Paid')} ✓${isOwner && player.paymentSource === 'manual' ? ' ✕' : ''}</span>` : ''}
                        </span>
                        <span class="player-category-badges" id="player-categories-${tournament.id}-${player.id}"></span>
                      </div>
                      <div class="player-row-actions">
                        ${highestRound === 0 ? `
                          <label class="present-checkbox" title="${t('player.presentTitle', 'Mark player as present/checked in')}">
                            <input type="checkbox" ${player.present ? 'checked' : ''} onchange="togglePlayerPresent('${tournament.id}', '${player.id}', this.checked)">
                            <span class="present-label">${t('player.present', 'Present')}</span>
                          </label>
                        ` : ''}
                        ${isOwner && !player.feePaid ? `
                          <button class="btn-paid-toggle" onclick="promptPlayerFeePaid('${tournament.id}', '${player.id}')" title="${t('player.markAsPaid', 'Mark entry fee as paid')}">
                            ${t('player.markPaid', '💰 Mark Paid')}
                          </button>
                        ` : ''}
                        ${tournament.format === TOURNAMENT_FORMATS.SWISS ? `
                          <button class="btn-icon" onclick="openProhibitedPairingsModal('${tournament.id}', '${player.id}')" title="${t('player.prohibitedPairings', 'Manage prohibited pairings')}">⊘</button>
                        ` : ''}
                        ${tournament.format === TOURNAMENT_FORMATS.SWISS && tournament.numberOfRounds ? `
                          <button class="btn-icon" onclick="openByeModal('${tournament.id}', '${player.id}')" title="${t('player.manageByes', 'Manage bye requests')}">${t('player.byeIcon', '⊗')}</button>
                        ` : ''}
                        ${isOwner && tournament.allowUserResultEntry && player.userId ? `
                          <button class="btn-icon ${player.canEnterResults !== false ? 'btn-icon-active' : ''}"
                            onclick="togglePlayerCanEnterResults('${tournament.id}', '${player.id}', ${player.canEnterResults !== false})"
                            title="${player.canEnterResults !== false ? t('player.disableResultEntry', 'Disable result entry') : t('player.enableResultEntry', 'Enable result entry')}">
                            ${player.canEnterResults !== false ? '✓' : '✗'}
                          </button>
                        ` : ''}
                        <button class="btn-icon" onclick="editPlayer('${tournament.id}', '${player.id}')" title="Edit player">✎</button>
                        <button class="btn-icon" onclick="removePlayer('${tournament.id}', '${player.id}')" title="Remove player">🗑</button>
                      </div>
                    </li>
                  `).join('')}
                </ul>
              ` : ''}
            </div>
          </div>

          <!-- Pairings Tab -->
          ${(highestRound > 0 || tournament.format === TOURNAMENT_FORMATS.SWISS || tournament.format === TOURNAMENT_FORMATS.ROUND_ROBIN || tournament.format === TOURNAMENT_FORMATS.KNOCKOUT) ? `
            <div id="pairings-tab-${tournament.id}" class="main-tab-pane">
              ${window.renderMatches ? window.renderMatches(tournament.matches, tournament.id, tournament.format, null, tournament.players.length, tournament.numberOfRounds, tournament) : ''}
            </div>

            <!-- Standings Tab -->
            <div id="standings-tab-${tournament.id}" class="main-tab-pane">
              <div id="standings-${tournament.id}">
                <p>Loading standings...</p>
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    </div>
  `;
}

/**
 * Initialize map for a tournament with coordinates
 * @param tournamentId - Tournament ID
 * @param latitude - Latitude
 * @param longitude - Longitude
 * @param venueName - Venue name for marker popup
 */
export function initializeTournamentMap(
  tournamentId: string,
  latitude: number,
  longitude: number,
  venueName?: string
): void {
  // Wait for Leaflet to be loaded
  const waitForLeaflet = () => {
    if (typeof window.L === 'undefined') {
      // Leaflet not loaded yet, try again in 50ms
      setTimeout(() => waitForLeaflet(), 50);
      return;
    }

    // Leaflet is loaded, now initialize the map
    initMap();
  };

  const initMap = () => {
    const mapElement = document.getElementById(`map-${tournamentId}`);
    if (!mapElement) {
      console.warn(`Map element not found: map-${tournamentId}`);
      return;
    }

    // Clear any existing map
    if ((mapElement as any)._leaflet_id) {
      return; // Map already initialized
    }

    try {
      // Create map centered on the location
      const map = window.L.map(`map-${tournamentId}`, {
        center: [latitude, longitude],
        zoom: 13,
        scrollWheelZoom: false,
        dragging: true,
        zoomControl: true,
        attributionControl: true
      });

      // Add OpenStreetMap tiles
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19
      }).addTo(map);

      // Add marker
      const marker = window.L.marker([latitude, longitude]).addTo(map);

      // Add popup with venue name if provided
      if (venueName) {
        marker.bindPopup(venueName).openPopup();
      }

      // Store map instance for later access
      (window as any)[`tournamentMap_${tournamentId}`] = map;

      // Fix for map not displaying correctly - wait for tiles to load
      setTimeout(() => {
        map.invalidateSize();
      }, 250);

      // Also invalidate on window resize
      const resizeHandler = () => {
        if (map) {
          map.invalidateSize();
        }
      };
      window.addEventListener('resize', resizeHandler);
    } catch (error) {
      console.error('Failed to initialize map:', error);
    }
  };

  // Start waiting for Leaflet
  waitForLeaflet();
}

// Export to window for use in other modules

/**
 * Load and display pending registrations for a tournament
 */
export async function loadPendingRegistrations(tournamentId: string): Promise<void> {
  const container = document.getElementById(`pending-registrations-${tournamentId}`);
  if (!container) return;

  try {
    // Dynamically import to avoid circular dependencies
    const { renderPendingRegistrations } = await import('./registrations-ui');
    const html = await renderPendingRegistrations(tournamentId);
    container.innerHTML = html;
  } catch (error) {
    console.error('Error loading pending registrations:', error);
  }
}

/**
 * Filter players in the player list
 * @param tournamentId - The tournament ID
 * @param query - The search query
 */
export function filterPlayers(tournamentId: string, query: string): void {
  // Delegate to filterPlayersByCategory which handles both text and category filtering
  if (window.filterPlayersByCategory) {
    window.filterPlayersByCategory(tournamentId, window.getActivePlayerCategoryFilter?.(tournamentId) || '');
  } else {
    // Fallback if category filter not loaded yet
    const list = document.getElementById(`player-list-ul-${tournamentId}`);
    if (!list) return;

    const rows = list.querySelectorAll('.player-row');
    const normalizedQuery = query.toLowerCase().trim();
    let visibleCount = 0;

    rows.forEach((row) => {
      const li = row as HTMLElement;
      const name = li.dataset.name || '';
      const rating = li.dataset.rating || '';
      const federation = li.dataset.federation || '';
      const club = li.dataset.club || '';
      const title = li.dataset.title || '';

      const matches = !normalizedQuery ||
        name.includes(normalizedQuery) ||
        rating.includes(normalizedQuery) ||
        federation.includes(normalizedQuery) ||
        club.includes(normalizedQuery) ||
        title.includes(normalizedQuery);

      li.style.display = matches ? '' : 'none';
      if (matches) visibleCount++;
    });

    // Update visible count
    const countSpan = document.querySelector(`#players-tab-${tournamentId} .player-count`);
    if (countSpan) {
      const totalCount = rows.length;
      if (normalizedQuery) {
        countSpan.textContent = `${visibleCount}/${totalCount}`;
      } else {
        countSpan.textContent = `${totalCount} ${totalCount === 1 ? t('player.player', 'player') : t('player.players', 'players')}`;
      }
    }
  }
}

/**
 * Filter players by present status
 * @param tournamentId - The tournament ID
 * @param filterValue - 'all', 'present', or 'notPresent'
 */
export function filterPlayersByPresent(tournamentId: string, filterValue: string): void {
  const list = document.getElementById(`player-list-ul-${tournamentId}`);
  if (!list) return;

  const rows = list.querySelectorAll('.player-row');
  const textFilter = (document.getElementById(`player-filter-${tournamentId}`) as HTMLInputElement)?.value.toLowerCase().trim() || '';
  let visibleCount = 0;

  rows.forEach((row) => {
    const li = row as HTMLElement;
    const isPresent = li.dataset.present === 'true';
    const name = li.dataset.name || '';
    const rating = li.dataset.rating || '';
    const federation = li.dataset.federation || '';
    const club = li.dataset.club || '';
    const title = li.dataset.title || '';

    // Check text filter
    const matchesText = !textFilter ||
      name.includes(textFilter) ||
      rating.includes(textFilter) ||
      federation.includes(textFilter) ||
      club.includes(textFilter) ||
      title.includes(textFilter);

    // Check present filter
    let matchesPresent = true;
    if (filterValue === 'present') {
      matchesPresent = isPresent;
    } else if (filterValue === 'notPresent') {
      matchesPresent = !isPresent;
    }

    const matches = matchesText && matchesPresent;
    li.style.display = matches ? '' : 'none';
    if (matches) visibleCount++;
  });

  // Update visible count
  const countSpan = document.querySelector(`#players-tab-${tournamentId} .player-count`);
  if (countSpan) {
    const totalCount = rows.length;
    if (filterValue !== 'all' || textFilter) {
      countSpan.textContent = `${visibleCount}/${totalCount}`;
    } else {
      countSpan.textContent = `${totalCount} ${totalCount === 1 ? t('player.player', 'player') : t('player.players', 'players')}`;
    }
  }
}

/**
 * Toggle description expand/collapse
 */
function toggleDescription(tournamentId: string): void {
  const wrapper = document.getElementById(`description-wrapper-${tournamentId}`);
  const button = document.getElementById(`description-toggle-${tournamentId}`);
  if (wrapper && button) {
    const isExpanded = wrapper.classList.toggle('expanded');
    button.classList.toggle('expanded', isExpanded);
    button.textContent = isExpanded
      ? (window.i18n?.t('tournament.readLess', 'Read less') || 'Read less')
      : (window.i18n?.t('tournament.readMore', 'Read more') || 'Read more');
  }
}

window.createTournamentCard = createTournamentCard;
window.createTournamentListCard = createTournamentListCard;
window.initializeTournamentMap = initializeTournamentMap;
window.loadPendingRegistrations = loadPendingRegistrations;
window.filterPlayers = filterPlayers;
window.filterPlayersByPresent = filterPlayersByPresent;
window.sortPlayers = sortPlayers;
window.toggleDescription = toggleDescription;
